import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 15; // Increased from 5 to 15 (30 seconds total)
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setError('Payment status check timed out. Please check your email for confirmation.');
      return;
    }

    try {
      console.log(`Polling payment status - attempt ${attempts + 1}/${maxAttempts}`);
      const response = await axios.get(`${API}/payments/checkout-status/${sessionId}`);
      const data = response.data;
      console.log('Payment status response:', data);

      if (data.payment_status === 'paid') {
        console.log('Payment confirmed, order_id:', data.order_id);
        setPaymentStatus('success');
        setOrderDetails({
          orderId: data.order_id || sessionId.substring(0, 8).toUpperCase(), // Fallback to session ID prefix
          amount: data.amount_total / 100,
          currency: (data.currency || 'cad').toUpperCase()
        });
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setError('Payment session expired. Please try again.');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      // On 404, payment may still be processing - continue polling if we have attempts left
      if (error.response?.status === 404 && attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        return;
      }
      setPaymentStatus('error');
      setError('Error checking payment status. Please contact support.');
    }
  };

  if (paymentStatus === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#f9f7ed' }}>
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center" data-testid="payment-checking">
          <Loader className="h-24 w-24 mx-auto mb-4 animate-spin" style={{ color: '#a80707' }} />
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#a80707' }}>Processing Payment...</h1>
          <p className="text-lg text-gray-600">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#f9f7ed' }}>
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center" data-testid="payment-error">
          <div className="mb-8">
            <div className="h-24 w-24 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fee' }}>
              <span className="text-4xl" style={{ color: '#a80707' }}>✕</span>
            </div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#a80707' }}>Payment Issue</h1>
            <p className="text-lg text-gray-600">{error}</p>
          </div>
          <div className="space-y-4">
            <Button
              onClick={() => navigate('/checkout')}
              className="w-full text-white font-semibold py-6 text-lg"
              style={{ backgroundColor: '#a80707' }}
              data-testid="retry-payment-btn"
            >
              Try Again
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full font-semibold py-6 text-lg"
              style={{ color: '#a80707', borderColor: '#a80707' }}
            >
              Back to Menu
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4" style={{ backgroundColor: '#f9f7ed' }}>
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-12 text-center" data-testid="payment-success-page">
        <div className="mb-8">
          <CheckCircle className="h-24 w-24 mx-auto mb-4" style={{ color: '#a80707' }} />
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#a80707' }}>Payment Successful!</h1>
          <p className="text-lg text-gray-600">Thank you for your order</p>
        </div>

        {orderDetails && (
          <div className="rounded-xl p-6 mb-8 text-left" style={{ backgroundColor: '#f9f7ed' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#a80707' }}>Order Confirmed</h2>
            <div className="space-y-2 text-gray-700">
              <p><span className="font-semibold">Order ID:</span> {orderDetails.orderId}</p>
              <p className="text-xl font-bold pt-2" style={{ color: '#a80707' }}>
                <span className="font-semibold text-gray-700">Amount Paid:</span> {orderDetails.currency}${orderDetails.amount.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-gray-600">
            Your order has been confirmed and we'll start preparing it right away.
            You'll receive an email confirmation shortly.
          </p>
          <Button
            onClick={() => navigate('/')}
            className="w-full text-white font-semibold py-6 text-lg"
            style={{ backgroundColor: '#a80707' }}
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;